import React from 'react';
import { Typography, Stack, Box } from '@mui/material';
import RadioRow from './RadioRow';
import Section from './Section';
import Field, { fieldControlStyle } from './Field';
import { CouponFormData } from './CouponFormPage';

interface FormFieldsProps {
  form: CouponFormData;
  update: (field: keyof CouponFormData, value: any) => void;
  mode: 'create' | 'edit';
}

export default function FormFields({ form, update, mode }: FormFieldsProps) {
  return (
    <>
      <Section title="Coupon Details">
        <Stack spacing={3}>
          <Field label="Coupon Code" required>
            <input
              type="text"
              style={{
                ...fieldControlStyle,
                ...(mode === 'edit' ? { backgroundColor: '#f9fafb', color: '#667085' } : {}),
              }}
              value={form.code}
              readOnly={mode === 'edit'}
              placeholder="e.g. SUMMER500"
              onChange={(e) => update('code', e.target.value)}
            />
          </Field>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ color: '#344054' }}>
              Promo Type
            </Typography>
            <RadioRow
              label=""
              value={form.promoType}
              onChange={(v) => {
                update('promoType', v as 'FIXED' | 'PERCENT' | 'ZERO_FEE');
                if (v === 'ZERO_FEE') update('amount', '0');
              }}
              options={[
                { value: 'FIXED', label: 'Fixed Amount' },
                { value: 'PERCENT', label: 'Percentage' },
                { value: 'ZERO_FEE', label: 'Zero Fee' },
              ]}
            />
          </Box>

          {form.promoType !== 'ZERO_FEE' && (
            <Field
              label={form.promoType === 'PERCENT' ? 'Percentage (%)' : 'Amount (₹)'}
              width={360}
            >
              <input
                type="number"
                style={fieldControlStyle}
                value={form.amount}
                onChange={(e) => update('amount', e.target.value)}
                placeholder="0"
              />
            </Field>
          )}
        </Stack>
      </Section>

      <Section title="Targeting">
        <Stack spacing={3}>
          <Box mt={3}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Applied On
            </Typography>
            <RadioRow
              label=""
              value={form.appliedOn}
              onChange={(v) =>
                update('appliedOn', v as CouponFormData['appliedOn'])
              }
              options={[
                { value: 'B2B', label: 'B2B' },
                { value: 'B2C', label: 'B2C' },
                { value: 'ALL', label: 'ALL' },
                { value: 'HIDDEN', label: 'HIDDEN' },
                { value: 'APP', label: 'APP' },
              ]}
            />
          </Box>
          <Field label="Channel" width={360}>
            <select
              style={{
                ...fieldControlStyle,
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23667085' d='M6 8L0 0h12z'/></svg>\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                paddingRight: 36,
                cursor: 'pointer',
              }}
              value={form.channel}
              onChange={(e) =>
                update('channel', e.target.value as CouponFormData['channel'])
              }
            >
              <option value="ALL">All Channels</option>
              <option value="WEB">Web Only</option>
              <option value="APP">Any App (iOS or Android)</option>
              <option value="IOS">iOS App Only</option>
              <option value="ANDROID">Android App Only</option>
            </select>
          </Field>
        </Stack>
      </Section>

      <Section title="Eligibility">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Field label="Booking From">
            <input
              type="date"
              style={fieldControlStyle}
              value={form.bookingFrom}
              onChange={(e) => update('bookingFrom', e.target.value)}
            />
          </Field>
          <Field label="Booking Until">
            <input
              type="date"
              style={fieldControlStyle}
              value={form.bookingUntil}
              onChange={(e) => update('bookingUntil', e.target.value)}
            />
          </Field>
        </Stack>
      </Section>

      <Section title="Usage Limits">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Field label="Total Limit" helperText="Leave blank for no limit">
            <input
              type="number"
              style={fieldControlStyle}
              value={form.maxUsage}
              onChange={(e) => update('maxUsage', e.target.value)}
              placeholder="No limit"
            />
          </Field>
          <Field label="Per User Limit" helperText="Leave blank for no limit">
            <input
              type="number"
              style={fieldControlStyle}
              value={form.maxUsagePerUser}
              onChange={(e) => update('maxUsagePerUser', e.target.value)}
              placeholder="No limit"
            />
          </Field>
        </Stack>
      </Section>
    </>
  );
}
